import { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';

// Lazy import pixel tracking — doesn't block initial render
const pixel = () => import('../utils/metaPixel');
const trackViewContent = (d) => pixel().then(m => m.trackViewContent(d)).catch(() => {});
const trackInitiateCheckout = (d) => pixel().then(m => m.trackInitiateCheckout(d)).catch(() => {});
const trackPurchase = (d) => pixel().then(m => m.trackPurchase(d)).catch(() => {});
const trackContact = (d) => pixel().then(m => m.trackContact(d)).catch(() => {});

export default function Public() {
  const { lang, setLang } = useLanguage();
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [playingSample, setPlayingSample] = useState(null);
  const audioRef = useRef(null);
  const reviewsRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [activeDot, setActiveDot] = useState(0);
  const [vinylTrack, setVinylTrack] = useState(0);
  const totalDots = 10;

  // Multi-step form
  const [step, setStep] = useState(0); // 0=landing, 1-5=form steps, 6=pre-checkout
  const [songLang, setSongLang] = useState('sl');
  const [voiceId, setVoiceId] = useState('');
  const [forWhom, setForWhom] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [genre, setGenre] = useState('');
  const [feeling, setFeeling] = useState('');
  const [qualities, setQualities] = useState([]);
  const [memories, setMemories] = useState('');
  const [message, setMessage] = useState('');
  const [voice, setVoice] = useState('');
  const [energy, setEnergy] = useState(50);
  const [pkg, setPkg] = useState(null);
  const [email, setEmail] = useState('');
  const [orderError, setOrderError] = useState('');
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [showSubscribePopup, setShowSubscribePopup] = useState(false);
  const [showBankInfo, setShowBankInfo] = useState(false);
  const [wantsInvoice, setWantsInvoice] = useState(false);
  const [invoiceName, setInvoiceName] = useState('');
  const [invoiceAddress, setInvoiceAddress] = useState('');
  const [invoiceTaxId, setInvoiceTaxId] = useState('');
  const [subEmail, setSubEmail] = useState('');
  const [subDone, setSubDone] = useState(false);
  const [subLoading, setSubLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  // Coupon state — captured on our form, validated against Stripe before checkout
  const [couponInput, setCouponInput] = useState('');
  const [couponChecking, setCouponChecking] = useState(false);
  const [couponApplied, setCouponApplied] = useState(null); // { code, promotion_code_id, percent_off, amount_off }
  const [couponError, setCouponError] = useState('');

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e);

  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
  const isHrPath = pathname.startsWith('/hr');
  const isEnPath = pathname.startsWith('/en');
  const isRsPath = pathname.startsWith('/rs');
  const basePath = isHrPath ? '/hr' : isEnPath ? '/en' : isRsPath ? '/rs' : '/';

  // Force language based on URL path: / = sl, /hr = hr, /en = en, /rs = hr (RS uses HR copy for now)
  useEffect(() => {
    if (isHrPath || isRsPath) setLang('hr');
    else if (isEnPath) setLang('en');
    else setLang('sl');
  }, []); // eslint-disable-line

  const sl = lang === 'sl';
  const hr = lang === 'hr';
  const t3 = (en, slov, cro) => hr ? cro : sl ? slov : en;
  const apiBase = '/api';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Browser back button support — push history on each step change
  useEffect(() => {
    if (step > 0) {
      window.history.pushState({ step }, '', basePath);
    }
  }, [step]);

  useEffect(() => {
    const handlePopState = (e) => {
      if (e.state && typeof e.state.step === 'number') {
        setStep(e.state.step);
      } else {
        setStep(0);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Meta Pixel: ViewContent when landing page loads
  useEffect(() => {
    trackViewContent({ contentName: 'Ustvari Pesem — Landing', contentCategory: 'Custom Song Service' });
  }, []);

  // First-party visitor tracking. Persistent visitor_id (localStorage) +
  // per-session session_id (sessionStorage). One beacon per page load —
  // backend drops it into the visits table for the dashboard's "returning
  // visitors" stats. Fire-and-forget; never blocks the user.
  useEffect(() => {
    try {
      let visitorId = localStorage.getItem('songcraft_visitor_id');
      if (!visitorId) {
        visitorId = 'v_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem('songcraft_visitor_id', visitorId);
      }
      let sessionId = sessionStorage.getItem('songcraft_session_id');
      if (!sessionId) {
        sessionId = 's_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
        sessionStorage.setItem('songcraft_session_id', sessionId);
      }
      let traffic = null;
      try { traffic = JSON.parse(localStorage.getItem('songcraft_traffic') || 'null'); } catch {}
      fetch(apiBase + '/track/visit', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitor_id: visitorId,
          session_id: sessionId,
          path: window.location.pathname,
          referrer: document.referrer || '',
          traffic_source: traffic?.source || '',
          utm_source:    traffic?.utm_source || '',
          utm_medium:    traffic?.medium || '',
          utm_campaign:  traffic?.campaign || '',
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Capture traffic source on first visit (UTM params + referrer + landing path)
  // Persists to localStorage so it's preserved across the form/checkout flow.
  // Re-captured if the visitor lands again with a NEW utm_source — that's
  // attribution best practice; the most recent paid touch wins.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const utm_source   = params.get('utm_source');
      const utm_medium   = params.get('utm_medium');
      const utm_campaign = params.get('utm_campaign');
      const utm_content  = params.get('utm_content');
      const utm_term     = params.get('utm_term');
      const fbclid       = params.get('fbclid');
      const gclid        = params.get('gclid');
      const ttclid       = params.get('ttclid');

      const hasUtm = utm_source || utm_medium || utm_campaign || fbclid || gclid || ttclid;
      const stored = (() => { try { return JSON.parse(localStorage.getItem('songcraft_traffic') || 'null'); } catch { return null; } })();

      if (hasUtm || !stored) {
        const referrer = document.referrer || '';
        const refHost = referrer ? (() => { try { return new URL(referrer).hostname; } catch { return ''; } })() : '';
        // Infer source from click ids if no utm_source provided
        const inferred = utm_source || (fbclid ? 'facebook' : gclid ? 'google' : ttclid ? 'tiktok' : refHost ? 'referral' : 'direct');
        const traffic = {
          source: inferred,
          medium: utm_medium || (fbclid || gclid || ttclid ? 'paid' : refHost ? 'referral' : 'direct'),
          campaign: utm_campaign || null,
          content: utm_content || null,
          term: utm_term || null,
          referrer: refHost || null,
          landing_path: window.location.pathname,
          first_seen_at: stored?.first_seen_at || new Date().toISOString(),
          last_seen_at: new Date().toISOString(),
          fbclid, gclid, ttclid,
        };
        localStorage.setItem('songcraft_traffic', JSON.stringify(traffic));
      }
    } catch {}
  }, []);

  // Show subscribe popup after 8 seconds (only on landing, not if already subscribed)
  useEffect(() => {
    if (localStorage.getItem('subscribed')) return;
    const timer = setTimeout(() => {
      if (step === 0) setShowSubscribePopup(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, [step]);

  // Meta Pixel: Contact event fires once when the user opens the song form.
  // Single-page flow no longer has a separate "submitted song details" step.
  useEffect(() => {
    if (step === 1) {
      trackContact({ contentName: `Song form opened — ${genre || 'no genre yet'}` });
    }
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = reviewsRef.current;
    if (!el) return;
    const onScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      const max = scrollWidth - clientWidth;
      setActiveDot(max > 0 ? Math.round((scrollLeft / max) * (totalDots - 1)) : 0);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [totalDots]);

  // Handle return from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const paid = params.get('paid');
    if (params.get('canceled')) { window.history.replaceState({}, '', basePath); return; }
    if (paid && sessionId) {
      (async () => {
        try {
          const res = await fetch(`${apiBase}/checkout/verify/${sessionId}`);
          const data = await res.json();
          if (data.paid) {
            // Only create order if webhook hasn't already (check _notified flag)
            if (!data.alreadyProcessed) {
              await fetch(apiBase + '/orders', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...data.orderData, email: data.email }),
              });
            }
            // Meta Pixel: Purchase after confirmed payment
            trackPurchase({ value: data.orderData?.price, currency: 'EUR', email: data.email, transactionId: sessionId });
            // Mark lead as converted
            fetch(apiBase + '/leads/converted', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: data.email }) }).catch(() => {});
            setEmail(data.email);
            setDone(true);
            // Wipe the saved draft now that the order is paid.
            try { localStorage.removeItem('songcraft_draft_v1'); } catch {}
          }
        } catch {}
        window.history.replaceState({}, '', basePath);
      })();
    }
  }, [apiBase]);

  // Cache Audio objects per URL so re-clicking a sample doesn't re-download
  // the 4-5 MB MP3. preload='auto' makes the browser start fetching the file
  // right after we instantiate it, so the first play() resolves much faster.
  const audioCacheRef = useRef({});
  const playSample = (url, id) => {
    if (playingSample === id) {
      audioRef.current?.pause();
      setPlayingSample(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    let a = audioCacheRef.current[url];
    if (!a) {
      a = new Audio(url);
      a.preload = 'auto';
      audioCacheRef.current[url] = a;
    }
    audioRef.current = a;
    a.onended = () => setPlayingSample(null);
    a.play().then(() => setPlayingSample(id)).catch(() => setPlayingSample(null));
    setPlayingSample(id);
  };

  // Prefetch the first sample's MP3 as soon as the landing page mounts so the
  // very first click feels instant. Cheap on mobile because the user has to
  // scroll to see the samples anyway.
  useEffect(() => {
    const first = basePath === '/rs' ? '/audio/staklo-na-autoputu.mp3'
                : basePath === '/hr' ? '/audio/hr/za-mamu-mariju.mp3'
                : '/audio/julia.mp3';
    if (!audioCacheRef.current[first]) {
      const a = new Audio();
      a.preload = 'metadata';
      a.src = first;
      audioCacheRef.current[first] = a;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubscribe = async () => {
    if (!subEmail.includes('@') || subLoading) return;
    setSubLoading(true);
    try {
      await fetch(apiBase + '/leads/subscribe', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: subEmail, lang }),
      });
      localStorage.setItem('subscribed', 'true');
      setSubDone(true);
    } catch {}
    setSubLoading(false);
  };

  // Self-serve pivot: send visitors to the new free create flow.
  // Old multi-step paid form remains in code below but is no longer reachable.
  const startForm = () => { window.location.href = '/create'; };

  // Save lead progress to DB at each step — only if we have a real email.
  // No email = no lead row (avoids anon-*@lead.local garbage in the dashboard).
  const saveLead = async (currentStep, extraData = {}) => {
    if (!email || !email.includes('@')) return;
    try {
      await fetch(apiBase + '/leads', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, step: currentStep, songLang, uiLang: lang, forWhom, recipientName, genre, feeling, voice, qualities, memories, message, energy, ...extraData }),
      });
    } catch {}
  };

  // Build story from form data
  const buildStory = () => {
    const parts = [];
    if (forWhom && recipientName) parts.push(`A song for my ${forWhom.toLowerCase()} named ${recipientName}.`);
    else if (forWhom) parts.push(`A song for my ${forWhom.toLowerCase()}.`);
    if (qualities.length) parts.push(`They are ${qualities.join(', ')}.`);
    if (memories) parts.push(`Special memories: ${memories}`);
    if (message) parts.push(`Message: ${message}`);
    return parts.join(' ');
  };

  const goToCheckout = async () => {
    if (!isValidEmail(email)) {
      setOrderError(t3('Please enter a valid email address', 'Vnesite veljaven e-naslov', 'Unesite valjanu e-adresu'));
      return;
    }
    // Meta Pixel: InitiateCheckout when user clicks payment button
    trackInitiateCheckout({ value: pkg?.price, currency: 'EUR', contentName: pkg?.name, email });
    setSubmitting(true);
    setOrderError('');
    try {
      const story = buildStory();
      let traffic = null;
      try { traffic = JSON.parse(localStorage.getItem('songcraft_traffic') || 'null'); } catch {}
      const res = await fetch(apiBase + '/checkout', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ songLang, reaction: feeling, story, genre, energy, voice, recipientName, packageId: pkg.id, packageName: pkg.name, songs: pkg.songs, includeVideo: pkg.video, delivery: pkg.delivery, email, price: pkg.price, returnPath: basePath === '/' ? '' : basePath, voiceId: voiceId || undefined, wantsInvoice, invoiceName, invoiceAddress, invoiceTaxId, promotion_code_id: couponApplied?.promotion_code_id || null, traffic }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Checkout failed');
      window.location.href = data.url;
    } catch (err) { setOrderError(err.message); }
    setSubmitting(false);
  };

  const closePopup = () => { setDone(false); setStep(0); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const samplesSL = [
    { id: 1, title: 'Julia', genre: 'Balada', mood: t3('Emotional', 'Čustvena', 'Emotivna'), url: '/audio/julia.mp3', img: '/images/songs/mother-daughter.jpg', desc: t3('A mother\'s ballad for her daughter — from the first day she held her, to an apology for the hard times. Her daughter heard her nickname "pikica" in the song and they both cried for half an hour.', 'Čustvena balada mame za hčerko — od prvega dne ko jo je držala v rokah, do opravičila za težke trenutke. Hčerka je slišala svoj vzdevek "pikica" v pesmi in obe sta jokali pol ure.', 'Emotivna balada mame za kćer — od prvog dana kad ju je držala u rukama, do isprike za teške trenutke.') },
    { id: 2, title: 'Edi', genre: 'Folk', mood: t3('Romantic', 'Romantična', 'Romantična'), url: '/audio/edi.mp3', img: '/images/songs/couple.jpg', desc: t3('A folk ballad about destiny — they met through his mother. About the first look that stopped the world and a love that only grew from that day.', 'Čustvena folk balada o sudbini ki ju je spojila preko njegove mame, o prvem pogledu ki je ustavil svet in o ljubezni ki je od tistega dne samo še rasla.', 'Emotivna folk balada o sudbini koja ih je spojila preko njegove mame, o prvom pogledu koji je zaustavio svijet.') },
    { id: 3, title: 'David', genre: 'Balada', mood: t3('Intimate', 'Intimna', 'Intimna'), url: '/audio/david.mp3', img: '/images/songs/couple2.jpg', desc: t3('An intimate ballad about coffee by the lake and blue eyes that became home. About a man who brought back her smile when she thought she lost it forever.', 'Intimna balada o kavici ob jezeru in o modrih očeh ki so ji postale dom. O človeku ki ji je vrnil nasmeh ko je mislila da ga je za vedno zgubila.', 'Intimna balada o kavici ob jezeru i o plavim očima koje su joj postale dom.') },
    { id: 4, title: 'Jaka', genre: 'Pop', mood: t3('Uplifting', 'Vzpodbudna', 'Inspirativna'), url: '/audio/jaka.mp3', img: '/images/songs/mother-son.jpg', desc: t3('A pop ballad from parents to their son — born premature, now a powerlifter with the biggest heart and the most beautiful smile. They\'ll love him forever.', 'Pop balada ki pove zgodbo od prezgodnjega rojstva do dvigovanja uteži. O fantku ki je zrasel v močnega moškega z največjim srcem in najlepšim nasmeham.', 'Pop balada koja priča priču od preuranjenog rođenja do dizanja utega. O dečku koji je izrastao u snažnog muškarca.') },
  ];

  const samplesHR = [
    { id: 101, title: 'Marija', genre: 'Balada', mood: 'Emotivna', url: '/audio/hr/za-mamu-mariju.mp3', img: '/images/songs/mother-daughter.jpg', desc: 'Emotivna balada za mamu — kolači nedjeljom, njezin osmijeh kad sam prvi put rekla mama. Hvala ti što si naš oslonac.' },
    { id: 102, title: 'Ivan', genre: 'Rock', mood: 'Energična', url: '/audio/hr/za-tatu-ivana.mp3', img: '/images/songs/mother-son.jpg', desc: 'Rock pjesma za tatu heroja — ribolov na Jadranu, garaža, prvi bicikl i lekcije hrabrosti.' },
    { id: 103, title: 'Ana', genre: 'Pop', mood: 'Romantična', url: '/audio/hr/za-suprugu-anu.mp3', img: '/images/songs/couple.jpg', desc: 'Pop pjesma za obljetnicu — 10 godina braka, koncert na Trgu bana Jelačića u Zagrebu. Sin Luka.' },
    { id: 104, title: 'Lana', genre: 'Folk', mood: 'Emotivna', url: '/audio/hr/za-ker-lanu.mp3', img: '/images/songs/couple2.jpg', desc: 'Folk pjesma za kćer za 18. rođendan — prvi koraci u dnevnoj sobi u Splitu. Od princeze do odrasle žene.' },
    { id: 105, title: 'Marko', genre: 'Hip-Hop', mood: 'Vesela', url: '/audio/hr/za-prijatelja-marka.mp3', img: '/images/songs/hr/marko.jpg', desc: 'Hip-hop za najboljeg prijatelja — osnovna škola u Rijeci, nogomet na Kantridi svake subote.' },
    { id: 106, title: 'Mia', genre: 'Latin', mood: 'Energična', url: '/audio/hr/za-djevojku-miju.mp3', img: '/images/songs/hr/mia.jpg', desc: 'Latin reggaeton za djevojku — salsa na ljetnoj terasi u Dubrovniku, oči koje su osvojile srce.' },
  ];

  // Serbian samples — for /rs only. First two slots use the user-supplied
  // Serbian audio; remaining slots fall through to the HR samples so the
  // carousel/grid still has 4+ items to show.
  const samplesRS = [
    { id: 201, title: 'Staklo na autoputu', genre: 'Balada', mood: 'Emotivna', url: '/audio/staklo-na-autoputu.mp3', img: '/images/songs/couple.jpg', desc: 'Emotivna balkanska balada — pesma za tihu vožnju kući, dok u mislima nosiš ljude koje najviše voliš.' },
    { id: 202, title: 'Neko bez glasa', genre: 'Balada', mood: 'Emotivna', url: '/audio/neko-bez-glasa.mp3', img: '/images/songs/mother-daughter.jpg', desc: 'Tiha balada o nekome ko nam je nedostajao — neko bez glasa, ali sa pričom koja ostaje u srcu zauvek.' },
    ...samplesHR.slice(2),
  ];

  const samples = basePath === '/rs' ? samplesRS : basePath === '/hr' ? samplesHR : samplesSL;

  const whomOptions = t3(
    ['Husband', 'Wife', 'Children', 'Boyfriend', 'Girlfriend', 'Father', 'Mother', 'Sibling', 'Friend', 'Myself', 'Other'],
    ['Mož', 'Žena', 'Otrok', 'Fant', 'Dekle', 'Oče', 'Mama', 'Brat/Sestra', 'Prijatelj', 'Jaz', 'Drugo'],
    ['Muž', 'Žena', 'Dijete', 'Dečko', 'Cura', 'Otac', 'Mama', 'Brat/Sestra', 'Prijatelj', 'Ja', 'Drugo']
  );

  const genreOptions = ['Pop', 'Rock', 'Country', 'R&B', 'Jazz', 'Hip-Hop', 'Folk', 'Balada', 'Latin'];

  const feelingOptions = t3(
    ['Happy', 'Emotional', 'Romantic', 'Fun', 'Motivational', 'Nostalgic', 'Deep', 'Energetic'],
    ['Vesela', 'Čustvena', 'Romantična', 'Zabavna', 'Motivacijska', 'Nostalgična', 'Globoka', 'Energična'],
    ['Vesela', 'Emotivna', 'Romantična', 'Zabavna', 'Motivacijska', 'Nostalgična', 'Duboka', 'Energična']
  );

  const qualityOptions = t3(
    ['Patient', 'Wise', 'Funny', 'Protective', 'Encouraging', 'Caring', 'Strong', 'Creative', 'Kind', 'Adventurous', 'Loyal', 'Generous'],
    ['Potrpežljiv/a', 'Moder/a', 'Zabaven/a', 'Zaščitniški/a', 'Spodbujajoč/a', 'Skrben/a', 'Močan/a', 'Ustvarjalen/a', 'Prijazen/a', 'Pustolovski/a', 'Zvest/a', 'Radodaren/a'],
    ['Strpljiv/a', 'Mudar/a', 'Zabavan/a', 'Zaštitnik/ica', 'Ohrabrujuć/a', 'Brižan/a', 'Jak/a', 'Kreativan/a', 'Ljubazan/a', 'Pustolovan/a', 'Vjeran/a', 'Velikodušan/a']
  );

  const toggleQuality = (q) => setQualities(prev => prev.includes(q) ? prev.filter(x => x !== q) : [...prev, q]);

  const applyCoupon = async () => {
    const code = (couponInput || '').trim();
    if (!code) return;
    setCouponChecking(true);
    setCouponError('');
    try {
      const res = await fetch(apiBase + '/checkout/validate-coupon', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (data.valid) {
        setCouponApplied(data);
      } else {
        setCouponApplied(null);
        setCouponError(t3('Invalid or expired code', 'Neveljavna ali potekla koda', 'Nevažeći ili istekli kod'));
      }
    } catch {
      setCouponError(t3('Could not check the code right now', 'Kode trenutno ni mogoče preveriti', 'Trenutno ne možemo provjeriti kod'));
    }
    setCouponChecking(false);
  };

  const removeCoupon = () => {
    setCouponApplied(null);
    setCouponInput('');
    setCouponError('');
  };

  // Save bank-transfer order once admin opens the bank info popup
  const saveBankOrder = async () => {
    setSubmitting(true);
    try {
      const story = buildStory();
      let traffic = null;
      try { traffic = JSON.parse(localStorage.getItem('songcraft_traffic') || 'null'); } catch {}
      const res = await fetch(apiBase + '/checkout/save-bank-order', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ songLang, reaction: feeling, story, genre, energy, voice, recipientName, packageName: pkg?.name, songs: pkg?.songs, includeVideo: pkg?.video, delivery: pkg?.delivery, email, price: pkg?.price, voiceId: voiceId || undefined, traffic }),
      });
      const data = await res.json();
      if (data.success) { setShowBankInfo(false); setDone(true); }
    } catch {}
    setSubmitting(false);
  };

  const packages = [
    { id: 1, name: 'Basic',   price: 15.99, songs: 1, video: false, delivery: '24h', time: t3('24 Hours', '24 ur',  '24 sata'), label: t3('MOST POPULAR', 'NAJBOLJ PRILJUBLJENO', 'NAJPOPULARNIJE'),    desc: t3('1 song + lyrics',                  '1 pesem + besedilo',                  '1 pjesma + tekst'),                pop: true },
    { id: 2, name: 'VIP',     price: 19.99, songs: 1, video: false, delivery: '4h',  time: t3('4 Hours',  '4 ure',  '4 sata'),  label: t3('EXPRESS',      'EKSPRESNO',           'EKSPRESNO'),         desc: t3('1 song + lyrics · priority',       '1 pesem + besedilo · prioriteta',     '1 pjesma + tekst · prioritet') },
    { id: 3, name: 'Premium', price: 24.99, songs: 2, video: false, delivery: '4h',  time: t3('4 Hours',  '4 ure',  '4 sata'),  label: t3('BEST VALUE',   'NAJBOLJŠA VREDNOST',  'NAJBOLJA VRIJEDNOST'), desc: t3('2 songs + lyrics · priority',       '2 pesmi + besedilo · prioriteta',     '2 pjesme + tekst · prioritet') },
  ];

  // Default selections so the optional fields don't have to be touched.
  // Runs once when the form opens (step === 1).
  useEffect(() => {
    if (step !== 1) return;
    if (!pkg) setPkg(packages[0]);
    if (!genre) setGenre('Balada');
    if (!feeling) setFeeling(feelingOptions[2]); // Romantic-ish
    if (!voice) setVoice('female');
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save form draft to localStorage so a phone lock-screen, tab
  // switch, or accidental refresh doesn't wipe everything. Restores on
  // mount. Cleared once the user pays (in the success flow).
  const DRAFT_KEY = 'songcraft_draft_v1';
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d.recipientName) setRecipientName(d.recipientName);
      if (d.memories) setMemories(d.memories);
      if (d.email) setEmail(d.email);
      if (d.forWhom) setForWhom(d.forWhom);
      if (d.genre) setGenre(d.genre);
      if (d.feeling) setFeeling(d.feeling);
      if (d.voice) setVoice(d.voice);
      if (d.songLang) setSongLang(d.songLang);
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (step < 1) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        recipientName, memories, email, forWhom, genre, feeling, voice, songLang,
      }));
    } catch {}
  }, [step, recipientName, memories, email, forWhom, genre, feeling, voice, songLang]);

  // ========== SINGLE-PAGE FORM ==========
  if (step >= 1) {
    const storyValid = (memories || '').trim().length >= 20;
    const canSubmit = !!recipientName.trim() && storyValid && isValidEmail(email) && pkg;

    return (
      <div className="min-h-screen text-gray-900" style={{ background: 'linear-gradient(180deg, #fef9f3 0%, #fdf3e8 30%, #fefcfa 100%)' }}>
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
            <button onClick={() => setStep(0)} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" d="M15 19l-7-7 7-7" /></svg>
              {t3('Back', 'Nazaj', 'Natrag')}
            </button>
            <h1 className="text-base font-bold" style={{ color: '#b45f2a' }}>
              {t3('Create your song', 'Ustvarite pesem', 'Stvorite pjesmu')}
            </h1>
            <span className="w-12" />
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-8 pb-24">
          {voiceId && (
            <div className="mb-6 bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3">
              <span className="text-2xl">🎤</span>
              <div>
                <p className="font-bold text-amber-900">{t3('Singer: Oliver Dragojević', 'Glas: Oliver Dragojević', 'Glas: Oliver Dragojević')}</p>
                <p className="text-xs text-amber-700">{t3('Lyrics will be in Croatian', 'Besedilo bo v hrvaškem jeziku', 'Tekst će biti na hrvatskom')}</p>
              </div>
            </div>
          )}

          {/* Live preview — updates as the buyer types so the song feels real */}
          <div className="mb-6 bg-white border-2 border-[#fdebd5] rounded-2xl p-4 flex items-center gap-3 shadow-sm">
            <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#b45f2a' }}>
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" /></svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-gray-900 text-[15px] truncate">
                {recipientName.trim()
                  ? t3(`Song for ${recipientName.trim()}`, `Pesem za ${recipientName.trim()}`, `Pjesma za ${recipientName.trim()}`)
                  : t3('Your song', 'Vaša pesem', 'Vaša pjesma')}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {[genre, feeling, pkg?.delivery].filter(Boolean).join(' · ')}
              </p>
            </div>
            <span className="font-bold text-lg flex-shrink-0" style={{ color: '#b45f2a' }}>
              €{(pkg?.price || 15.99).toFixed(2)}
            </span>
          </div>

          {/* Recipient name — auto-capitalize first letter, green check when filled */}
          <div className="mb-5">
            <label className="block text-[15px] font-semibold mb-2">
              {t3("Their first name", 'Njihovo ime', 'Njihovo ime')} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input type="text" value={recipientName}
                onChange={e => {
                  const v = e.target.value;
                  // Auto-capitalize first letter, leave the rest of the field alone
                  setRecipientName(v.length > 0 ? v.charAt(0).toUpperCase() + v.slice(1) : v);
                }}
                autoComplete="off" autoCorrect="off" spellCheck={false}
                className={`w-full border-2 bg-white rounded-xl px-4 py-4 pr-12 focus:outline-none transition-colors text-[16px] ${recipientName.trim() ? 'border-green-400 focus:border-green-500' : 'border-gray-300 focus:border-[#b45f2a]'}`}
                placeholder={t3('e.g. Anna, Marko', 'npr. Ana, Marko', 'npr. Ana, Marko')}
                autoFocus />
              {recipientName.trim() && (
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500" aria-label="valid">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" d="M5 13l4 4L19 7" /></svg>
                </span>
              )}
            </div>
          </div>

          {/* Who is it for (small chips, optional) */}
          <div className="mb-5">
            <label className="block text-sm font-semibold mb-2">{t3('Who is it for?', 'Komu je namenjeno?', 'Kome je namijenjeno?')}</label>
            <div className="flex flex-wrap gap-2">
              {whomOptions.map(w => (
                <button key={w} type="button" onClick={() => setForWhom(w)}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${forWhom === w ? 'text-white border-transparent' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'}`}
                  style={forWhom === w ? { backgroundColor: '#b45f2a', borderColor: '#b45f2a' } : {}}>
                  {w}
                </button>
              ))}
            </div>
          </div>

          {/* Story — the one big textarea */}
          <div className="mb-5">
            <label className="block text-sm font-semibold mb-2">
              {t3('Tell their story', 'Povejte njihovo zgodbo', 'Ispričajte njihovu priču')} <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-500 mb-2">
              {t3(
                'How you met, special moments, nicknames, inside jokes, what makes them special, what you want to say. The more details, the better the song.',
                'Kako ste se spoznali, posebni trenutki, vzdevki, šale, kaj jih dela posebne, kaj jim želite povedati. Več podrobnosti = boljša pesem.',
                'Kako ste se upoznali, posebni trenuci, nadimci, šale, što ih čini posebnima, što im želite reći. Više detalja = bolja pjesma.'
              )}
            </p>
            <textarea value={memories} onChange={e => setMemories(e.target.value)}
              className="w-full border border-gray-300 bg-white rounded-xl px-4 py-3.5 focus:outline-none focus:border-[#b45f2a] transition-colors min-h-[180px] resize-none text-base"
              placeholder={t3(
                'Example: We met in college and have been together for 8 years. He proposed in Paris under the Eiffel tower. He calls me "lemon" because of the time I made awful lemon cake. He is my best friend, my biggest support, the kindest soul I know.',
                'Primer: Spoznala sva se na faksu in sva skupaj 8 let. V Parizu pod Eifflovim stolpom me je zaprosil. Imenuje me "limona" zaradi grozne limonine torte. Je moj najboljši prijatelj.',
                'Primjer: Upoznali smo se na faksu i zajedno smo 8 godina. Zaprosio me u Parizu pod Eiffelovim tornjem. Zove me "limun" zbog grozne torte od limuna. Moj je najbolji prijatelj.'
              )}
            />
            <p className={`text-xs mt-1 ${storyValid ? 'text-green-600' : 'text-gray-400'}`}>
              {(memories || '').trim().length} / 20 {t3('characters minimum', 'znakov minimum', 'znakova minimum')}
              {storyValid ? ' ✓' : ''}
            </p>
          </div>

          {/* Style — genre, mood, voice in compact row */}
          <div className="mb-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold mb-2">{t3('Genre', 'Žanr', 'Žanr')}</label>
              <select value={genre} onChange={e => setGenre(e.target.value)}
                className="w-full border border-gray-300 bg-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#b45f2a] transition-colors text-base">
                {genreOptions.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2">{t3('Mood', 'Vzdušje', 'Raspoloženje')}</label>
              <select value={feeling} onChange={e => setFeeling(e.target.value)}
                className="w-full border border-gray-300 bg-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#b45f2a] transition-colors text-base">
                {feelingOptions.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </div>

          {/* Voice gender */}
          <div className="mb-5">
            <label className="block text-sm font-semibold mb-2">{t3('Voice', 'Glas', 'Glas')}</label>
            <div className="grid grid-cols-2 gap-3">
              {[{ k: 'female', l: t3('Female', 'Ženski', 'Ženski') }, { k: 'male', l: t3('Male', 'Moški', 'Muški') }].map(v => (
                <button key={v.k} type="button" onClick={() => setVoice(v.k)}
                  className={`py-3 rounded-xl font-medium border transition-all ${voice === v.k ? 'text-white border-transparent' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'}`}
                  style={voice === v.k ? { backgroundColor: '#b45f2a', borderColor: '#b45f2a' } : {}}>
                  {v.l}
                </button>
              ))}
            </div>
          </div>

          {/* Song language */}
          <div className="mb-6">
            <label className="block text-sm font-semibold mb-2">{t3('Song language', 'Jezik pesmi', 'Jezik pjesme')}</label>
            <div className="grid grid-cols-3 gap-2">
              {[{ k: 'sl', l: 'Slovenščina' }, { k: 'hr', l: 'Hrvatski' }, { k: 'en', l: 'English' }].map(o => (
                <button key={o.k} type="button" onClick={() => setSongLang(o.k)}
                  className={`py-2.5 rounded-xl text-sm font-medium border transition-all ${songLang === o.k ? 'text-white border-transparent' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'}`}
                  style={songLang === o.k ? { backgroundColor: '#b45f2a', borderColor: '#b45f2a' } : {}}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>

          {/* Package selection */}
          <div className="mb-6">
            <label className="block text-sm font-semibold mb-3">
              {t3('Choose delivery speed', 'Izberite hitrost dostave', 'Odaberite brzinu dostave')} <span className="text-red-500">*</span>
            </label>
            <div className="space-y-3">
              {packages.map(p => (
                <button key={p.id} type="button" onClick={() => setPkg(p)}
                  className={`relative w-full text-left px-5 py-4 rounded-xl border-2 transition-all ${pkg?.id === p.id ? 'border-[#b45f2a] bg-[#fdf6ef]' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                  {p.label && <span className="absolute -top-2.5 right-4 text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: '#b45f2a' }}>{p.label}</span>}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-lg text-gray-900">{p.time}</p>
                      <p className="text-gray-500 text-xs mt-0.5">{p.desc}</p>
                    </div>
                    <span className="font-black text-xl" style={{ color: '#b45f2a' }}>€{p.price.toFixed(2)}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Email — auto-trim, green check when valid */}
          <div className="mb-5">
            <label className="block text-[15px] font-semibold mb-2">
              {t3('Your email', 'Vaš e-naslov', 'Vaša e-adresa')} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input type="email" value={email}
                onChange={e => { setEmail(e.target.value.trim()); if (emailError) setEmailError(''); }}
                autoCapitalize="off" autoCorrect="off" spellCheck={false} inputMode="email"
                className={`w-full border-2 bg-white rounded-xl px-4 py-4 pr-12 focus:outline-none transition-colors text-[16px] ${isValidEmail(email) ? 'border-green-400 focus:border-green-500' : 'border-gray-300 focus:border-[#b45f2a]'}`}
                placeholder={t3('you@email.com', 'vas@email.si', 'vas@email.hr')} />
              {isValidEmail(email) && (
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500" aria-label="valid">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" d="M5 13l4 4L19 7" /></svg>
                </span>
              )}
            </div>
            {email && !isValidEmail(email) && (
              <p className="text-xs text-orange-600 mt-1.5">
                {t3('Hmm, that email looks incomplete — make sure it has @ and a domain (e.g. anna@gmail.com)',
                    'Hmm, ta e-naslov je nepopoln — preverite, da vsebuje @ in domeno (npr. ana@gmail.com)',
                    'Hmm, ta e-mail izgleda nepotpuno — provjerite da sadrži @ i domenu (npr. ana@gmail.com)')}
              </p>
            )}
            <div className="flex items-center gap-1.5 mt-2">
              <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
              <p className="text-xs text-gray-400">{t3('We respect your privacy. No spam.', 'Spoštujemo vašo zasebnost. Brez neželene pošte.', 'Poštujemo vašu privatnost. Bez neželjene pošte.')}</p>
            </div>
          </div>

          {/* Invoice */}
          <div className="mb-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={wantsInvoice} onChange={e => setWantsInvoice(e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 accent-[#b45f2a]" />
              <span className="text-sm text-gray-700">{t3('I need an invoice', 'Potrebujem račun', 'Trebam račun')}</span>
            </label>
            {wantsInvoice && (
              <div className="mt-3 space-y-3 pl-8">
                <input type="text" value={invoiceName} onChange={e => setInvoiceName(e.target.value)}
                  className="w-full border border-gray-300 bg-white rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#b45f2a]"
                  placeholder={t3('Full name or company name', 'Ime in priimek ali podjetje', 'Ime i prezime ili tvrtka')} />
                <input type="text" value={invoiceAddress} onChange={e => setInvoiceAddress(e.target.value)}
                  className="w-full border border-gray-300 bg-white rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#b45f2a]"
                  placeholder={t3('Address', 'Naslov', 'Adresa')} />
                <input type="text" value={invoiceTaxId} onChange={e => setInvoiceTaxId(e.target.value)}
                  className="w-full border border-gray-300 bg-white rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#b45f2a]"
                  placeholder={t3('Tax ID (optional)', 'Davčna številka (neobvezno)', 'OIB (neobvezno)')} />
              </div>
            )}
          </div>

          {/* Coupon — visible by default so people don't miss it. Replaces the
              old "Add code" button on the Stripe page. */}
          <div className="mb-5">
            {couponApplied ? (
              <div className="bg-green-50 border-2 border-green-300 rounded-xl px-4 py-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" d="M5 13l4 4L19 7" /></svg>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-green-800">
                    {t3('Coupon applied:', 'Koda uveljavljena:', 'Kod primijenjen:')} {couponApplied.code}
                  </p>
                  <p className="text-xs text-green-700">
                    {couponApplied.percent_off
                      ? `−${couponApplied.percent_off}% ${t3('off', 'popust', 'popust')}`
                      : couponApplied.amount_off
                        ? `−€${(couponApplied.amount_off / 100).toFixed(2)} ${t3('off', 'popust', 'popust')}`
                        : t3('Discount applied', 'Popust uveljavljen', 'Popust primijenjen')}
                  </p>
                </div>
                <button onClick={removeCoupon} className="text-xs text-green-700 hover:text-green-900 font-medium underline flex-shrink-0">
                  {t3('Remove', 'Odstrani', 'Ukloni')}
                </button>
              </div>
            ) : (
              <>
                <label className="flex items-center gap-1.5 text-[15px] font-semibold mb-2">
                  <svg className="w-4 h-4 text-[#b45f2a]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
                  {t3('Coupon code', 'Koda za popust', 'Kupon za popust')}
                  <span className="text-xs font-normal text-gray-400">({t3('optional', 'neobvezno', 'neobavezno')})</span>
                </label>
                <div className="flex gap-2">
                  <input type="text" value={couponInput}
                    onChange={e => { setCouponInput(e.target.value.toUpperCase()); if (couponError) setCouponError(''); }}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); applyCoupon(); } }}
                    autoCapitalize="characters" autoCorrect="off" spellCheck={false}
                    className="flex-1 border-2 border-gray-300 bg-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#b45f2a] transition-colors text-[15px] uppercase tracking-wider"
                    placeholder={t3('e.g. SAVE50-XXXXXX', 'npr. SAVE50-XXXXXX', 'npr. SAVE50-XXXXXX')} />
                  <button onClick={applyCoupon} disabled={!couponInput.trim() || couponChecking}
                    className={`px-5 py-3 rounded-xl font-bold text-sm transition-colors flex-shrink-0 ${couponInput.trim() && !couponChecking ? 'text-white' : 'bg-gray-200 text-gray-500'}`}
                    style={couponInput.trim() && !couponChecking ? { backgroundColor: '#b45f2a' } : {}}>
                    {couponChecking ? '…' : t3('Apply', 'Uveljavi', 'Primijeni')}
                  </button>
                </div>
                {couponError && <p className="text-xs text-red-600 mt-1.5">{couponError}</p>}
              </>
            )}
          </div>

          {/* Real-time "what's missing" hint — far less anxiety-inducing than
              just disabling the pay button silently. Lists each missing field
              in plain language so non-technical users know exactly what to do. */}
          {!canSubmit && (recipientName || memories || email) && (() => {
            const missing = [];
            if (!recipientName.trim()) missing.push(t3('first name', 'ime', 'ime'));
            if (!storyValid) missing.push(t3('a few sentences in their story', 'nekaj stavkov o njih', 'nekoliko rečenica o njima'));
            if (!isValidEmail(email)) missing.push(t3('a valid email', 'veljaven e-naslov', 'valjana e-adresa'));
            if (missing.length === 0) return null;
            return (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-xl px-4 py-3 mb-4 flex items-start gap-2">
                <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <span>
                  <strong>{t3('Almost there!', 'Skoraj!', 'Skoro!')}</strong>{' '}
                  {t3('Still missing: ', 'Še manjka: ', 'Još fali: ')}
                  {missing.join(', ')}.
                </span>
              </div>
            );
          })()}

          {orderError && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-xl px-4 py-3 mb-4">{orderError}</div>}

          {/* Trust strip — quick reassurance directly above the pay buttons. */}
          <div className="flex items-center justify-center flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500 mb-2">
            <span className="inline-flex items-center gap-1">
              <svg className="w-3.5 h-3.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" d="M5 13l4 4L19 7" /></svg>
              {t3('SSL secure', 'SSL zaščita', 'SSL zaštita')}
            </span>
            <span>·</span>
            <span className="inline-flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><rect x="2" y="6" width="20" height="12" rx="2"/><path d="M2 10h20"/></svg>
              {t3('Powered by Stripe', 'Stripe plačila', 'Stripe plaćanja')}
            </span>
            <span>·</span>
            <span className="inline-flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
              {t3('Encrypted', 'Šifrirano', 'Šifrirano')}
            </span>
          </div>
          <p className="text-[11px] text-gray-500 text-center mb-4 leading-relaxed">
            {t3(
              'We never see or store your card. Stripe (used by Apple, Amazon, Booking) processes your payment with bank-level encryption.',
              'Vaših podatkov o kartici ne vidimo in ne shranjujemo. Stripe (uporablja Apple, Amazon, Booking) obdela plačilo z bančno enkripcijo.',
              'Vaše podatke kartice ne vidimo niti pohranjujemo. Stripe (koriste Apple, Amazon, Booking) obrađuje plaćanje s bankovnom enkripcijom.'
            )}
          </p>

          {/* Pay buttons */}
          <button onClick={() => { saveLead(1); goToCheckout(); }} disabled={!canSubmit || submitting}
            className={`w-full py-4 rounded-xl font-bold text-base transition-all flex items-center justify-center gap-3 mb-3 ${canSubmit && !submitting ? 'text-white hover:shadow-xl hover:scale-[1.01]' : 'bg-gray-200 text-gray-500'}`}
            style={canSubmit && !submitting ? { backgroundColor: '#b45f2a' } : {}}>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
            {submitting ? t3('Redirecting…', 'Preusmerjanje…', 'Preusmjeravanje…')
              : pkg ? `${t3('Pay with Card', 'Plačaj s kartico', 'Plati karticom')} · €${pkg.price.toFixed(2)}`
              : t3('Select a package', 'Izberite paket', 'Odaberite paket')}
          </button>

          <button onClick={() => { if (canSubmit) { saveLead(1); setShowBankInfo(true); } }} disabled={!canSubmit}
            className={`w-full py-4 rounded-xl font-bold text-base transition-all border-2 flex items-center justify-center gap-3 ${canSubmit ? 'border-gray-300 text-gray-700 hover:bg-gray-50' : 'bg-gray-100 text-gray-400 border-gray-200'}`}>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" /></svg>
            {pkg ? `${t3('Pay by Bank Transfer', 'Plačaj z bančnim nakazilom', 'Plati bankovnim prijenosom')} · €${pkg.price.toFixed(2)}` : t3('Select a package', 'Izberite paket', 'Odaberite paket')}
          </button>

          {/* Money-back pill — single, prominent, green */}
          <div className="mt-5 mb-3 mx-auto max-w-fit bg-green-50 border border-green-200 rounded-full px-4 py-2 flex items-center gap-2">
            <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
            <span className="text-sm text-green-800 font-bold">
              {t3('30-day money-back guarantee', '30-dnevna garancija vračila denarja', '30-dnevna garancija povrata novca')}
            </span>
          </div>
          <p className="text-xs text-gray-500 text-center mb-6">
            {t3('Secure payment via Stripe. Apple Pay & Google Pay accepted.', 'Varno plačilo preko Stripe. Apple Pay in Google Pay sprejeta.', 'Sigurno plaćanje preko Stripe. Apple Pay i Google Pay prihvaćeni.')}
          </p>

          {/* Sample songs to build trust */}
          <div className="border-t border-gray-200 pt-6">
            <p className="text-sm font-semibold mb-4 text-center">
              {t3('Listen to songs we made for real customers', 'Poslušajte pesmi ki smo jih naredili za prave stranke', 'Poslušajte pjesme koje smo napravili za prave klijente')}
            </p>
            <div className="grid grid-cols-2 gap-3">
              {samples.slice(0, 4).map(s => (
                <button key={s.id} onClick={() => playSample(s.url, s.id)}
                  className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-3 py-2.5 hover:border-[#d4a574] transition-colors text-left">
                  <img src={s.img} alt={s.title} width="48" height="48" loading="lazy" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-gray-900 truncate">{s.title}</p>
                    <p className="text-xs text-gray-500">{s.genre} · {s.mood}</p>
                  </div>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#b45f2a' }}>
                    {playingSample === s.id ? (
                      <svg width="12" height="12" fill="white" viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
                    ) : (
                      <svg width="12" height="12" fill="white" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Bank transfer info popup — kept from before */}
        {showBankInfo && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowBankInfo(false)} />
            <div className="relative bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <button onClick={() => setShowBankInfo(false)} className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100">
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
              <div className="text-center">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: '#fdf6ef' }}>
                  <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="#b45f2a" strokeWidth="2"><path strokeLinecap="round" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" /></svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">{t3('Bank Transfer Payment', 'Plačilo z bančnim nakazilom', 'Plaćanje bankovnim prijenosom')}</h3>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 my-4 text-left">
                  <p className="text-amber-800 text-sm font-bold mb-1">{t3('Important', 'Pomembno', 'Važno')}</p>
                  <p className="text-amber-700 text-sm">
                    {t3(
                      'Your song details and order information will be saved for 24 hours. After completing the bank transfer, please send us a payment confirmation via email or WhatsApp so we can process your song faster.',
                      'Podatki o pesmi in naročilu bodo shranjeni 24 ur. Po opravljenem bančnem nakazilu nam prosimo pošljite potrdilo o plačilu po e-pošti ali WhatsApp.',
                      'Podaci o pjesmi i narudžbi bit će spremljeni 24 sata. Nakon izvršene bankove uplate, molimo pošaljite nam potvrdu o plaćanju putem e-maila ili WhatsAppa.'
                    )}
                  </p>
                </div>
                <div className="text-left space-y-3 mb-4">
                  {[
                    { label: t3('Amount', 'Znesek', 'Iznos'), value: `€${pkg?.price.toFixed(2)}`, copy: pkg?.price.toFixed(2) },
                    { label: t3('Recipient', 'Prejemnik', 'Primatelj'), value: 'BROTHERHOOD d.o.o.', copy: 'BROTHERHOOD d.o.o.' },
                    { label: 'IBAN', value: 'SI56028430265713347', copy: 'SI56028430265713347' },
                    { label: 'BIC / SWIFT', value: 'LJBASI2X', copy: 'LJBASI2X' },
                    { label: t3('Bank', 'Banka', 'Banka'), value: 'NLB d.d., Ljubljana, Slovenia', copy: 'NLB d.d., Ljubljana, Slovenia' },
                  ].map(({ label, value, copy }) => (
                    <div key={label} className="flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-400">{label}</p>
                        <p className="text-sm font-bold text-gray-900 truncate">{value}</p>
                      </div>
                      <button onClick={() => { navigator.clipboard.writeText(copy); }}
                        className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors">
                        {t3('Copy', 'Kopiraj', 'Kopiraj')}
                      </button>
                    </div>
                  ))}
                </div>
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-left mb-4">
                  <p className="text-green-800 text-sm font-bold mb-2">{t3('After payment, send confirmation to:', 'Po plačilu pošljite potrdilo na:', 'Nakon uplate pošaljite potvrdu na:')}</p>
                  <a href="mailto:info@ustvari-pesem.si" className="block text-green-700 text-sm font-medium hover:underline">info@ustvari-pesem.si</a>
                  <a href="https://wa.me/38670126994" target="_blank" rel="noopener noreferrer" className="block text-green-700 text-sm font-medium hover:underline">WhatsApp: +386 70 126 994</a>
                </div>
                <button onClick={saveBankOrder} disabled={submitting}
                  className="w-full py-3.5 rounded-xl font-bold text-base text-white hover:shadow-lg transition-all disabled:opacity-50"
                  style={{ backgroundColor: '#b45f2a' }}>
                  {submitting ? t3('Saving…', 'Shranjevanje…', 'Spremanje…') : t3('Save My Order', 'Shrani moje naročilo', 'Spremi moju narudžbu')}
                </button>
                <p className="text-xs text-gray-400 mt-2">{t3('Your order will be saved for 24 hours', 'Vaše naročilo bo shranjeno 24 ur', 'Vaša narudžba bit će spremljena 24 sata')}</p>
              </div>
            </div>
          </div>
        )}

        {/* Floating WhatsApp button — visible on the form page too so people
            can ask a question before paying. Same number as the footer/landing. */}
        <a href="https://wa.me/38670126994" target="_blank" rel="noopener noreferrer"
          aria-label={t3('Chat on WhatsApp', 'Vprašajte na WhatsApp', 'Pitajte na WhatsApp')}
          className="fixed bottom-5 right-5 z-50 w-14 h-14 bg-green-500 rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 hover:scale-110 transition-all">
          <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        </a>
      </div>
    );
  }

  // ========== LANDING PAGE ==========
  return (
    <main className="min-h-screen bg-white text-gray-900">

      {/* SUBSCRIBE POPUP — 20% discount */}
      {showSubscribePopup && !subDone && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center px-4 pb-4 sm:pb-0">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowSubscribePopup(false)} />
          <div className="relative bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 max-w-sm w-full shadow-2xl" onClick={e => e.stopPropagation()}>
            <button aria-label="Close" onClick={() => setShowSubscribePopup(false)} className="absolute top-3 right-3 w-11 h-11 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-600 hover:bg-gray-100">
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <div className="text-center mb-5">
              <div className="text-4xl mb-3">🎵</div>
              <h3 className="text-xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'Georgia, serif' }}>
                {t3('Get 20% Off!', 'Pridobite 20% popust!', 'Ostvarite 20% popust!')}
              </h3>
              <p className="text-gray-500 text-sm">
                {t3('Subscribe and receive an exclusive discount code for your personalized song.',
                     'Prijavite se in prejmite ekskluzivno kodo za popust na personalizirano pesem.',
                     'Prijavite se i primite ekskluzivni kod za popust na personaliziranu pjesmu.')}
              </p>
            </div>
            <input
              type="email" value={subEmail} onChange={e => setSubEmail(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && subEmail.includes('@')) handleSubscribe(); }}
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-base focus:outline-none focus:border-[#b45f2a] transition-colors mb-3 text-gray-900"
              placeholder={t3('you@email.com', 'vas@email.si', 'vas@email.hr')}
              autoFocus
            />
            <button
              onClick={() => { if (subEmail.includes('@')) handleSubscribe(); }}
              disabled={!subEmail.includes('@') || subLoading}
              className={`w-full py-3 rounded-xl font-bold text-base transition-all ${subEmail.includes('@') ? 'text-white hover:shadow-lg' : 'bg-gray-200 text-gray-500'}`}
              style={subEmail.includes('@') && !subLoading ? { backgroundColor: '#b45f2a' } : {}}>
              {subLoading ? '...' : t3('Get My 20% Discount', 'Pridobi 20% popust', 'Ostvari 20% popust')}
            </button>
            <p className="text-[10px] text-gray-500 text-center mt-3">{t3('No spam. Unsubscribe anytime.', 'Brez neželene pošte. Odjava kadarkoli.', 'Bez neželjene pošte. Odjava bilo kada.')}</p>
          </div>
        </div>
      )}

      {/* SUBSCRIBE SUCCESS */}
      {showSubscribePopup && subDone && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center px-4 pb-4 sm:pb-0">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowSubscribePopup(false)} />
          <div className="relative bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 max-w-sm w-full shadow-2xl text-center" onClick={e => e.stopPropagation()}>
            <div className="text-4xl mb-3">🎉</div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{t3('Check your email!', 'Preverite e-pošto!', 'Provjerite e-poštu!')}</h3>
            <p className="text-gray-500 text-sm mb-1">{t3('Your 20% discount code:', 'Vaša koda za 20% popust:', 'Vaš kod za 20% popust:')}</p>
            <p className="text-2xl font-black tracking-widest mb-4" style={{ color: '#b45f2a' }}>POPUST20</p>
            <button onClick={() => { setShowSubscribePopup(false); startForm(); }}
              className="w-full py-3 rounded-xl font-bold text-white text-base" style={{ backgroundColor: '#b45f2a' }}>
              {t3('Create Your Song Now', 'Ustvari pesem zdaj', 'Stvori pjesmu sada')} →
            </button>
          </div>
        </div>
      )}

      {/* SUCCESS POPUP */}
      {done && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4" onClick={closePopup}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="relative bg-white rounded-3xl p-8 sm:p-10 max-w-md w-full shadow-2xl text-center" onClick={e => e.stopPropagation()}>
              <>
                <div className="w-20 h-20 rounded-full bg-[#b45f2a] flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#b45f2a]/20/50">
                  <svg width="36" height="36" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                </div>
                <h2 className="text-2xl font-bold mb-3">{t3('Order submitted!', 'Naročilo oddano!', 'Narudžba poslana!')}</h2>
                <p className="text-gray-500 mb-2">{t3('Your song will be sent to:', 'Pesem bo poslana na:', 'Pjesma će biti poslana na:')}</p>
                <p className="text-lg font-bold text-[#b45f2a] mb-4">{email}</p>
                <div className="bg-[#fdf6ef] border border-[#e8d5c0] rounded-xl px-4 py-3 mb-6">
                  <p className="text-[#8b4513] text-sm font-medium">{t3('You will receive your song by email within the delivery time of your package.', 'Pesem boste prejeli po e-pošti v času dostave vašega paketa.', 'Pjesmu ćete primiti e-poštom u vremenu dostave vašeg paketa.')}</p>
                </div>
                <button onClick={closePopup} className="w-full py-3.5 text-white font-bold rounded-lg text-lg" style={{ backgroundColor: '#b45f2a' }}>{t3('OK', 'V redu', 'U redu')}</button>
              </>
          </div>
        </div>
      )}

      {/* ANNOUNCEMENT BAR */}
      <div className="bg-gray-900 text-white text-center py-2 text-sm font-medium">
        {t3('Create your personalized song — free to try, pay only to download', 'Ustvari personalizirano pesem — brezplačno preizkusi, plačaš le za prenos', 'Stvori personaliziranu pjesmu — besplatno isprobaj, plati samo za preuzimanje')}
        <button onClick={startForm} className="ml-2 underline font-bold hover:text-[#d4a574] transition-colors">{t3('Start now', 'Začni zdaj', 'Počni sada')} →</button>
      </div>

      {/* NAVIGATION */}
      <nav className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-sm' : 'bg-white/80 backdrop-blur-md'}`}>
        <div className="max-w-6xl mx-auto px-3 sm:px-6 flex items-center justify-between gap-2 h-16">
          <a href="/" aria-label="Ustvari Pesem — Home" className="flex items-center flex-shrink-0">
            <svg className="w-[120px] sm:w-[200px]" viewBox="0 0 320 120" xmlns="http://www.w3.org/2000/svg">
              <rect x="24" y="50" width="4" height="20" fill="#d97706" rx="2"/><rect x="33" y="42" width="4" height="36" fill="#f59e0b" rx="2"/>
              <rect x="42" y="34" width="4" height="52" fill="#d97706" rx="2"/><rect x="51" y="42" width="4" height="36" fill="#f59e0b" rx="2"/>
              <rect x="60" y="50" width="4" height="20" fill="#d97706" rx="2"/>
              <text x="84" y="54" fontFamily="Georgia, serif" fontSize="28" fontWeight="700" fill="#111827" letterSpacing="-0.5">ustvari</text>
              <text x="84" y="78" fontFamily="Georgia, serif" fontSize="28" fontWeight="400" fill="#d97706" letterSpacing="-0.5" fontStyle="italic">pesem</text>
            </svg>
          </a>
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0 min-w-0">
            <a href="#reviews" className="hidden sm:block text-sm text-gray-500 hover:text-gray-900">{t3('Reviews', 'Ocene', 'Recenzije')}</a>
            <a href="#samples" className="hidden sm:block text-sm text-gray-500 hover:text-gray-900">{t3('Samples', 'Primeri', 'Uzorci')}</a>
            <div className="flex items-center gap-0.5 sm:gap-1 text-xs">
              {[
                { code: 'SL', path: '/' },
                { code: 'HR', path: '/hr' },
                { code: 'RS', path: '/rs' },
                { code: 'EN', path: '/en' },
              ].map(o => (
                <button key={o.code} aria-label={`Language: ${o.code}`} onClick={() => { if (basePath !== o.path) window.location.href = o.path; }}
                  className={`px-1.5 sm:px-2.5 py-1 sm:py-1.5 rounded font-medium text-[10px] sm:text-xs min-w-[28px] sm:min-w-[36px] min-h-[32px] sm:min-h-[36px] flex items-center justify-center ${basePath === o.path ? 'text-white' : 'text-gray-500 hover:text-gray-700'}`}
                  style={basePath === o.path ? { backgroundColor: '#b45f2a' } : {}}>{o.code}</button>
              ))}
            </div>
            {/* CTA: full text on >=sm, short "Start" on phones so the arrow stays visible */}
            <button onClick={startForm} aria-label={t3('Create Your Song', 'Ustvari pesem', 'Stvori pjesmu')}
              className="text-white font-semibold px-2.5 py-1.5 sm:px-5 sm:py-2 rounded-lg text-xs sm:text-sm hover:shadow-lg transition-all whitespace-nowrap inline-flex items-center gap-1 flex-shrink-0" style={{ backgroundColor: '#b45f2a' }}>
              <span className="hidden sm:inline">{t3('Create Your Song', 'Ustvari pesem', 'Stvori pjesmu')}</span>
              <span className="sm:hidden">{t3('Start', 'Začni', 'Počni')}</span>
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
            </button>
          </div>
        </div>
      </nav>

      {/* HERO — mobile-first, compact, fast */}
      <section className="pt-6 md:pt-8 pb-10 md:pb-16 px-4" style={{ backgroundColor: '#fdf6ef' }}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-6 md:gap-12">

          {/* Left column — text */}
          <div className="flex-1 text-center md:text-left">
            {/* Inline mini-review — instant social proof on mobile */}
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm rounded-full px-3 py-1.5 mb-4 border border-gray-200 shadow-sm">
              <div className="flex gap-0.5">{[1,2,3,4,5].map(i => <svg key={i} className="w-3 h-3" fill="#c2590a" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>)}</div>
              <span className="text-xs text-gray-600 font-medium">{t3('500+ songs created', '500+ ustvarjenih pesmi', '500+ stvorenih pjesama')}</span>
            </div>

            <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold leading-[1.15] mb-3 md:mb-5 text-gray-900" style={{ fontFamily: 'Georgia, serif' }}>
              {hr ? 'Poklon koji nikada neće zaboraviti'
                   : sl ? 'Darilo ki ga ne bo nikoli pozabil/a'
                   : 'A Gift They Will Never Forget'}
            </h1>

            {/* Mobile: play sample inline */}
            <div className="md:hidden mb-5">
              <button onClick={() => playSample(samples[0]?.url, samples[0]?.id)}
                className="w-full flex items-center gap-3 bg-white rounded-xl border border-gray-200 px-4 py-3 shadow-sm active:scale-[0.98] transition-transform">
                <div className="w-11 h-11 rounded-full flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: '#b45f2a' }}>
                  {playingSample === samples[0]?.id ? (
                    <svg width="16" height="16" fill="white" viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
                  ) : (
                    <svg width="16" height="16" fill="white" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                  )}
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-bold text-gray-900">{t3('Listen to a real song', 'Poslušajte pravo pesem', 'Poslušajte pravu pjesmu')}</p>
                  <p className="text-xs text-gray-500">{samples[0]?.title} · {samples[0]?.genre}</p>
                </div>
                <div className="flex gap-[2px] items-end h-5">
                  {[3,5,4,6,3,5,4].map((h,i) => <div key={i} className="w-[3px] rounded-full" style={{ height: `${h*3}px`, backgroundColor: playingSample === samples[0]?.id ? '#b45f2a' : '#d1d5db', animation: playingSample === samples[0]?.id ? `pulse 0.${4+i}s ease-in-out infinite alternate` : 'none' }} />)}
                </div>
              </button>
            </div>

            {/* CTA buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start mb-4 md:mb-6">
              <button onClick={startForm} className="text-white font-bold py-4 px-10 rounded-xl text-lg hover:shadow-xl hover:scale-105 active:scale-[0.98] transition-all inline-flex items-center justify-center gap-2 shadow-lg shadow-[#b45f2a]/20" style={{ backgroundColor: '#b45f2a' }}>
                {t3('Create Your Song — Free', 'Ustvari pesem — brezplačno', 'Stvori pjesmu — besplatno')}
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
              </button>
              <a href="#how" className="hidden md:inline-flex font-semibold py-4 px-8 rounded-lg text-lg border-2 border-gray-300 text-gray-700 hover:border-gray-400 transition-all items-center justify-center gap-2">
                {t3('See How It Works', 'Kako deluje', 'Kako funkcionira')}
              </a>
            </div>

            {/* Trust signals — compact row */}
            <div className="flex items-center gap-3 justify-center md:justify-start text-xs text-gray-500 mb-4">
              <span className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" d="M5 13l4 4L19 7" /></svg>
                {t3('30-day guarantee', '30-dnevna garancija', '30-dnevna garancija')}
              </span>
              <span className="text-gray-300">|</span>
              <span className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" d="M5 13l4 4L19 7" /></svg>
                {t3('Delivered in 24h', 'Dostava v 24h', 'Dostava za 24h')}
              </span>
              <span className="text-gray-300">|</span>
              <span className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" d="M5 13l4 4L19 7" /></svg>
                {t3('2 min to order', '2 min za naročilo', '2 min za narudžbu')}
              </span>
            </div>

            {/* Mobile mini-review quote */}
            <div className="md:hidden bg-white rounded-xl border border-gray-100 p-3 mb-4 shadow-sm">
              <div className="flex items-start gap-3">
                <img src="/images/reviews/petra.jpg" alt="Petra L." width="36" height="36" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-600 leading-relaxed italic">
                    "{sl ? 'Jokali sva obe pol ure. Najboljše darilo ki sem ga kdaj dala.' : hr ? 'Plakale smo obje pola sata. Najbolji poklon ikad.' : 'We both cried for half an hour. Best gift I ever gave.'}"
                  </p>
                  <p className="text-[10px] text-gray-500 mt-1 font-medium">Petra L. <span className="text-green-500">✓</span></p>
                </div>
              </div>
            </div>

            {/* Desktop: avatars + stars */}
            <div className="hidden md:flex items-center gap-4 mt-6 justify-start">
              <div className="flex items-center -space-x-3">
                {['/images/reviews/maja.jpg','/images/reviews/petra.jpg','/images/reviews/simona.jpg','/images/reviews/tjasa.jpg'].map((src, i) => (
                  <img key={i} src={src} alt="" width="40" height="40" loading="lazy" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                ))}
                <div className="w-10 h-10 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-500">+498</div>
              </div>
              <div>
                <div className="flex items-center gap-0.5">
                  {[1,2,3,4,5].map(i => <svg key={i} className="w-4 h-4" fill="#c2590a" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>)}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{t3('Loved by 500+ families', 'Več kot 500 zadovoljnih družin', 'Više od 500 zadovoljnih obitelji')}</p>
              </div>
            </div>
          </div>

          {/* Right column — video (desktop only, hidden on mobile for speed) */}
          <div className="hidden md:block flex-1 w-full max-w-xl">
            <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-[4/3]">
              <video className="w-full h-full object-cover" playsInline autoPlay loop muted preload="none"
                ref={el => { if (el && !el.dataset.loaded) { el.dataset.loaded = '1'; const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.src = '/videos/hero.mp4'; obs.disconnect(); } }); obs.observe(el); } }} />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4 pointer-events-none">
                <p className="text-white text-sm font-medium">{t3('Real Customer Reactions', 'Pristne reakcije strank', 'Stvarne reakcije klijenata')}</p>
              </div>
              <button aria-label="Toggle sound" onClick={(e) => { const vid = e.target.closest('.relative').querySelector('video'); vid.muted = !vid.muted; e.target.closest('button').querySelector('.sound-on').classList.toggle('hidden'); e.target.closest('button').querySelector('.sound-off').classList.toggle('hidden'); }}
                className="absolute bottom-4 right-4 w-11 h-11 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-md hover:bg-white transition-colors">
                <svg className="sound-off w-5 h-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" /><path strokeLinecap="round" strokeLinejoin="round" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" /></svg>
                <svg className="sound-on w-5 h-5 text-gray-700 hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728" /></svg>
              </button>
            </div>
          </div>

        </div>

          {/* Partner logos */}
          <div className="max-w-4xl mx-auto mt-8 md:mt-12 text-center border-t border-gray-200 pt-5 md:pt-6">
            <p className="text-[10px] md:text-xs tracking-[0.15em] uppercase text-gray-500 font-semibold mb-3 md:mb-4">{t3('FEATURED ON LOCAL NEWS', 'OMENJENI V MEDIJIH', 'U LOKALNIM MEDIJIMA')}</p>
            <div className="flex items-center justify-center gap-8 sm:gap-16">
              <img src="/logos/24ur.png" alt="24ur" width="80" height="48" loading="lazy" className="h-12 sm:h-16 object-contain" />
              <img src="/logos/kanala.png" alt="Kanal A" width="80" height="48" loading="lazy" className="h-12 sm:h-16 object-contain" />
              <img src="/logos/planettv.png" alt="Planet TV" width="80" height="48" loading="lazy" className="h-12 sm:h-16 object-contain" />
              <img src="/logos/rtv.png" alt="RTV" width="80" height="48" loading="lazy" className="h-12 sm:h-16 object-contain" />
            </div>
          </div>
      </section>

      {/* STICKY MOBILE CTA */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-t border-gray-200 px-4 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]" style={{ display: step >= 1 ? 'none' : undefined }}>
        <button onClick={startForm} className="w-full text-white font-bold py-3.5 rounded-xl text-base active:scale-[0.98] transition-all shadow-lg shadow-[#b45f2a]/20 flex items-center justify-center gap-2" style={{ backgroundColor: '#b45f2a' }}>
          {t3('Create Your Song — Free', 'Ustvari pesem — brezplačno', 'Stvori pjesmu — besplatno')}
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
        </button>
      </div>

      {/* OLIVER DRAGOJEVIĆ — Croatian page only.
          Hidden during the self-serve pivot (shows a fixed €25.99 price + uses paid form).
          Can be revived as a premium "voice" option later. */}
      {false && basePath === '/hr' && step === 0 && (
        <section className="py-12 md:py-16 px-4" style={{ backgroundColor: '#fdf6ef' }}>
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="flex flex-col md:flex-row items-center">
                {/* Left — Image */}
                <div className="flex-shrink-0 md:w-[340px] w-full">
                  <div className="relative">
                    <img src="/images/oliver.jpg" alt="Oliver Dragojević" className="w-full h-[300px] md:h-[420px] object-cover" />
                    <div className="absolute top-4 left-4 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg" style={{ backgroundColor: '#b45f2a' }}>
                      LIMITIRANA PONUDA
                    </div>
                  </div>
                  {/* Sample song player */}
                  <div className="p-3 border-t border-gray-100" style={{ backgroundColor: '#fef9f3' }}>
                    <p className="text-xs text-gray-500 mb-2 text-center">Poslušajte primjer</p>
                    <audio controls className="w-full" src="/audio/hr/oliver-sample.mp3" preload="metadata" style={{ height: '36px' }}>
                      Your browser does not support audio.
                    </audio>
                  </div>
                </div>

                {/* Right — Text + CTA */}
                <div className="flex-1 p-6 md:p-10 text-center md:text-left">
                  <p className="text-sm font-semibold tracking-wider uppercase mb-2" style={{ color: '#b45f2a' }}>Ekskluzivno na Stvori Pjesmu</p>
                  <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4" style={{ fontFamily: 'Georgia, serif', lineHeight: 1.2 }}>
                    Stvori pjesmu glasom<br />
                    <span style={{ color: '#b45f2a' }}>Olivera Dragojevića</span>
                  </h2>
                  <p className="text-gray-500 text-base mb-6 max-w-lg">
                    Napišite svoju priču, a mi ćemo stvoriti jedinstvenu pjesmu s glasom koji je obilježio generacije. Savršen poklon za nekoga posebnog.
                  </p>
                  <div className="flex items-center gap-4 mb-6 justify-center md:justify-start">
                    <span className="text-4xl font-extrabold" style={{ color: '#b45f2a' }}>€25,99</span>
                    <span className="text-lg text-gray-300 line-through">€39,99</span>
                    <span className="text-white text-xs font-bold px-3 py-1 rounded-full" style={{ backgroundColor: '#b45f2a' }}>-35%</span>
                  </div>
                  <button
                    onClick={() => {
                      setLang('hr'); setSongLang('hr');
                      setVoiceId('d96e5187d62425b302b786ccfef2c4c3');
                      setGenre('Balada'); setFeeling('Emotivna'); setVoice('male');
                      setEnergy(40); setQualities(['Vjeran/a', 'Velikodušan/a']);
                      setPkg({ id: 99, name: 'Oliver Dragojević', songs: 1, video: false, delivery: '24h', price: 25.99 });
                      setStep(1);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all text-white"
                    style={{ backgroundColor: '#b45f2a' }}>
                    Stvori pjesmu s Oliverovim glasom →
                  </button>
                  <div className="flex items-center gap-2 mt-4 justify-center md:justify-start">
                    <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" d="M5 13l4 4L19 7" /></svg>
                    <span className="text-sm text-gray-500">Isporuka za 24 sata</span>
                    <span className="text-gray-300 mx-1">·</span>
                    <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" d="M5 13l4 4L19 7" /></svg>
                    <span className="text-sm text-gray-500">MP3 + tekst pjesme</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* HOW IT WORKS — compact version. Heavy padding + long copy was making
          this section feel like an entire page on mobile. Now: tighter
          spacing, smaller cards, one-line descriptions, smaller CTA. */}
      <section id="how" className="py-8 md:py-12 px-4" style={{ backgroundColor: '#fdf6ef' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-extrabold mb-1" style={{ fontFamily: 'Georgia, serif' }}>
            {t3('3 Easy Steps', '3 koraki do pesmi', '3 koraka do pjesme')}
          </h2>
          <p className="text-gray-500 text-sm mb-6">
            {t3('From your story to a finished song', 'Od vaše zgodbe do gotove pesmi', 'Od vaše priče do gotove pjesme')}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
            {[
              { n: '1', icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
                t: t3('Tell Us Your Story', 'Povejte nam zgodbo', 'Ispričajte nam priču'),
                sub: t3('2 min', '2 min', '2 min'),
                d: t3('A short form — who they are, what connects you.', 'Kratki obrazec — kdo so, kaj vaju povezuje.', 'Kratki obrazac — tko su, što vas povezuje.') },
              { n: '2', icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" /></svg>,
                t: t3('We Craft Your Song', 'Mi ustvarimo pesem', 'Mi stvaramo pjesmu'),
                sub: t3('Your genre', 'Vaš žanr', 'Vaš žanr'),
                d: t3('Lyrics + a professional song in your chosen style.', 'Besedilo in profesionalna pesem po vaši izbiri.', 'Tekst i profesionalna pjesma po vašem izboru.') },
              { n: '3', icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
                t: t3('Receive & Surprise', 'Prejemite in presenetite', 'Primite i iznenadite'),
                sub: t3('In 24h', 'V 24 urah', 'Za 24 sata'),
                d: t3('By email. Press play and watch the reaction.', 'Po e-pošti. Pritisnite play in opazujte reakcijo.', 'E-poštom. Pritisnite play i gledajte reakciju.') },
            ].map(s => (
              <div key={s.n} className="relative bg-white rounded-xl border border-gray-200 p-4 text-left flex items-center gap-3 sm:flex-col sm:text-center sm:gap-2 sm:p-5">
                <div className="absolute top-2 right-2 sm:-top-2.5 sm:left-2.5 sm:right-auto w-6 h-6 rounded-md flex items-center justify-center text-white font-bold text-[11px]" style={{ backgroundColor: '#b45f2a' }}>{s.n}</div>
                <div className="w-10 h-10 rounded-full bg-[#fdf6ef] flex items-center justify-center flex-shrink-0 sm:mx-auto sm:mb-1" style={{ color: '#b45f2a' }}>{s.icon}</div>
                <div className="flex-1 min-w-0 pr-7 sm:pr-0">
                  <h3 className="font-bold text-[15px] leading-tight">{s.t}</h3>
                  <p className="text-[11px] font-semibold mb-0.5" style={{ color: '#b45f2a' }}>{s.sub}</p>
                  <p className="text-gray-500 text-xs leading-snug">{s.d}</p>
                </div>
              </div>
            ))}
          </div>

          <button onClick={startForm} className="text-white font-bold py-3 px-7 rounded-lg text-base hover:shadow-xl hover:scale-105 transition-all" style={{ backgroundColor: '#b45f2a' }}>
            {t3('Begin Your Song', 'Začnite svojo pesem', 'Započnite svoju pjesmu')} →
          </button>
        </div>
      </section>

      {/* WATCH REAL REACTIONS — videos + songs carousel */}
      <section id="reviews" className="py-12 md:py-20 px-4" style={{ backgroundColor: '#fdf6ef' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-extrabold text-center mb-4 text-gray-900" style={{ fontFamily: 'Georgia, serif' }}>
            {t3(<>Watch Real Reactions. <span className="italic" style={{ color: '#b45f2a' }}>Feel the Emotion.</span></>,
                 <>500+ Slovencev je že <span className="italic" style={{ color: '#b45f2a' }}>razjokalo svoje najbližje</span></>,
                 <>500+ ljudi je već <span className="italic" style={{ color: '#b45f2a' }}>rasplakalo svoje najbliže</span></>)}
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            {t3(<>These aren't actors. These are <strong className="text-gray-900">real people</strong> receiving their personalized song for the first time. <em>Imagine creating this moment for someone you love.</em></>,
                 <>To niso igralci. To so <strong className="text-gray-900">pravi ljudje</strong>, ki prvič slišijo svojo pesem. <em>Predstavljajte si, da ustvarite ta trenutek za nekoga, ki ga imate radi.</em></>,
                 <>Ovo nisu glumci. Ovo su <strong className="text-gray-900">pravi ljudi</strong> koji prvi put čuju svoju pjesmu. <em>Zamislite da stvorite taj trenutak za nekoga koga volite.</em></>)}
          </p>

          {/* Scrollable carousel — alternating: song, video, song, video, song, video, song */}
          <div ref={reviewsRef} className="flex gap-4 overflow-x-auto pb-6 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
            {[
              { type: 'song', ...samples[0] },
              { type: 'video', src: '/videos/ad1.webm', title: t3('A Gift of Song', 'Darilo v pesmi', 'Poklon u pjesmi') },
              { type: 'song', ...samples[1] },
              { type: 'video', src: '/videos/ad2.webm', title: t3('Tears of Joy', 'Solze sreče', 'Suze sreće') },
              { type: 'song', ...samples[2] },
              { type: 'video', src: '/videos/ad3.mp4', title: t3('From the Heart', 'Iz srca', 'Iz srca') },
              { type: 'song', ...samples[3] },
            ].map((card, i) => (
              <div key={i} className="flex-shrink-0 snap-start" style={{ width: card.type === 'video' ? '220px' : '260px' }}>
                {card.type === 'video' ? (
                  <div className="relative rounded-2xl overflow-hidden bg-gray-900 cursor-pointer" style={{ height: '380px' }}>
                    <video src={card.src} className="w-full h-full object-cover" playsInline loop muted preload="metadata"
                      onClick={(e) => { const vid = e.target; if (vid.paused) { vid.muted = false; vid.play().catch(() => {}); } else { vid.pause(); vid.muted = true; } }} />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 pointer-events-none">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-white/20 backdrop-blur-sm">
                          <svg width="14" height="14" fill="white" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                        </div>
                        <p className="text-white font-bold text-sm">{card.title}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm flex flex-col" style={{ minHeight: '380px' }}>
                    <div className="relative h-40 overflow-hidden">
                      <img src={card.img} alt={card.title} width="260" height="160" loading="lazy" className="w-full h-full object-cover" />
                      <button aria-label={`Play ${card.title}`} onClick={() => playSample(card.url, card.id)}
                        className="absolute bottom-3 right-3 w-11 h-11 rounded-full flex items-center justify-center shadow-lg" style={{ backgroundColor: '#b45f2a' }}>
                        {playingSample === card.id ? (
                          <svg width="14" height="14" fill="white" viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
                        ) : (
                          <svg width="14" height="14" fill="white" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                        )}
                      </button>
                    </div>
                    <div className="p-4 flex-1 flex flex-col">
                      <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#b45f2a' }}>{card.genre} · {card.mood}</p>
                      <h3 className="font-bold text-gray-900 mb-2" style={{ fontFamily: 'Georgia, serif' }}>{card.title}</h3>
                      <p className="text-gray-500 text-xs leading-relaxed flex-1">{card.desc}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Navigation dots */}
          <div className="flex justify-center gap-2 mt-6">
            {Array.from({ length: totalDots }).map((_, i) => (
              <button key={i} aria-label={`Scroll to page ${i + 1}`} onClick={() => {
                const el = reviewsRef.current;
                if (!el) return;
                const max = el.scrollWidth - el.clientWidth;
                el.scrollTo({ left: (i / (totalDots - 1)) * max, behavior: 'smooth' });
              }}
              className="w-8 h-8 flex items-center justify-center"
              ><span className={`block w-2.5 h-2.5 rounded-full transition-all ${i === activeDot ? 'scale-110' : ''}`} style={{ backgroundColor: i === activeDot ? '#b45f2a' : '#d1d5db' }} /></button>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <p className="text-gray-700 mb-4">
              <strong className="text-gray-900">{t3('Your loved one deserves this moment.', 'Vaša draga oseba si zasluži ta trenutek.', 'Vaša draga osoba zaslužuje ovaj trenutak.')}</strong> {t3('The smile. The tears of joy. The memory they\'ll treasure forever.', 'Nasmeh. Solze sreče. Spomin, ki ga bodo za vedno cenili.', 'Osmijeh. Suze sreće. Uspomena koju će zauvijek čuvati.')}
            </p>
            <button onClick={startForm} className="text-white font-bold py-4 px-10 rounded-lg text-lg hover:shadow-xl hover:scale-105 transition-all" style={{ backgroundColor: '#b45f2a' }}>
              {t3('Create Their Unforgettable Moment', 'Ustvarite njihov nepozaben trenutek', 'Stvorite njihov nezaboravan trenutak')}
            </button>
            <p className="text-gray-500 text-sm mt-3">{t3('Tears of happiness guaranteed, or your money back', 'Solze sreče zagotovljene, ali denar nazaj', 'Suze sreće zajamčene, ili novac natrag')}</p>
          </div>
        </div>
      </section>

      {/* URGENCY — limited slots */}
      <section className="py-6 md:py-10 px-4" style={{ backgroundColor: '#fdf6ef' }}>
        <div className="max-w-xl mx-auto bg-red-50 border border-red-200 rounded-2xl p-5 md:p-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xl">⚠️</span>
            <p className="font-bold text-red-800">
              {t3('Our songwriters can only produce 10 songs at a time', 'Naši tekstopisci lahko ustvarijo le 10 pesmi hkrati', 'Naši tekstopisci mogu proizvesti samo 10 pjesama odjednom')}
            </p>
          </div>
          <div className="w-full h-3 bg-red-100 rounded-full mb-3">
            <div className="h-full rounded-full bg-gradient-to-r from-yellow-400 via-orange-500 to-red-600" style={{ width: '82%' }} />
          </div>
          <p className="text-sm mb-4">
            <span className="font-bold text-red-600 text-lg">{t3('4 left', 'Še 4 prosta', 'Još 4 slobodna')}</span>
            <span className="text-red-700"> — {t3('next slots open tomorrow', 'naslednja mesta jutri', 'sljedeća mjesta sutra')}</span>
          </p>
          <button onClick={startForm} className="w-full text-white font-bold py-3.5 rounded-xl text-lg hover:shadow-xl hover:scale-[1.02] transition-all" style={{ backgroundColor: '#b45f2a' }}>
            {t3('Reserve Your Slot Now', 'Rezervirajte svoje mesto', 'Rezervirajte svoje mjesto')}
          </button>
        </div>
      </section>

      {/* SAMPLE SONGS */}
      <section id="samples" className="py-12 md:py-20 px-4" style={{ backgroundColor: '#fdf6ef' }}>
        <div className="max-w-5xl mx-auto">
          <p className="text-center text-xs tracking-[0.2em] uppercase mb-3" style={{ color: '#b45f2a' }}>{t3('HEAR FOR YOURSELF', 'POSLUŠAJTE SAMI', 'POSLUŠAJTE SAMI')}</p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-center mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            {t3(<>Listen to Some of Our <span className="italic" style={{ color: '#b45f2a' }}>Favorites</span></>,
                 <>Poslušajte nekaj naših <span className="italic" style={{ color: '#b45f2a' }}>najljubših</span></>,
                 <>Poslušajte neke od naših <span className="italic" style={{ color: '#b45f2a' }}>najdražih</span></>)}
          </h2>
          <p className="text-center text-gray-500 mb-12 max-w-xl mx-auto">{t3('Every song tells a unique story. Which one speaks to you?', 'Vsaka pesem pripoveduje edinstveno zgodbo. Katera vas nagovori?', 'Svaka pjesma priča jedinstvenu priču. Koja vam govori?')}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5">
            {samples.map((s, i) => (
              <div key={i} className="group cursor-pointer text-center">
                <div className="relative rounded-2xl overflow-hidden bg-gray-100 mb-3 shadow-sm" style={{ aspectRatio: '3/4' }}>
                  <img src={s.img} alt={s.title} width="300" height="400" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <button aria-label={`Play ${s.title}`} onClick={(e) => { e.stopPropagation(); playSample(s.url, s.id); }}
                      className="w-14 h-14 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      {playingSample === s.id ? (
                        <svg width="20" height="20" fill="#b45f2a" viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
                      ) : (
                        <svg width="20" height="20" fill="#b45f2a" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                      )}
                    </button>
                  </div>
                </div>
                <p className="font-bold text-sm text-gray-900">{s.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">{s.genre} · {s.mood}</p>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed px-1">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CUSTOMER REVIEWS */}
      <section className="py-12 md:py-20 px-4" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-extrabold text-center mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            {t3(<>What Our <span className="italic" style={{ color: '#b45f2a' }}>Customers</span> Say</>,
                 <>Kaj pravijo naše <span className="italic" style={{ color: '#b45f2a' }}>stranke</span></>,
                 <>Što kažu naši <span className="italic" style={{ color: '#b45f2a' }}>klijenti</span></>)}
          </h2>
          <div className="flex items-center justify-center gap-2 mb-12">
            <div className="flex gap-0.5">{[1,2,3,4,5].map(i => <svg key={i} className="w-5 h-5" fill="#c2590a" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>)}</div>
            <span className="text-sm text-gray-500 ml-1">{t3('5.0 from 500+ reviews', '5.0 iz 500+ ocen', '5.0 iz 500+ recenzija')}</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: 'Maja K.', img: '/images/reviews/maja.jpg',
                text: 'Svojemu fotru sem naredila pesem za 50. rojstni dan. Napisali so kako je on tisti ki popravla vse od pipe do življenja in kako nikol ne tarna. Ko smo mu predvajali na zabavi je vstal, me objel in reku "to je najboljša stvar ki sem jo kdaj dobil". Vsi smo jokali. Folk žanr je bil popoln.' },
              { name: 'Tjaša R.', img: '/images/reviews/tjasa.jpg',
                text: 'Naredila sem pesem za fanta za najino obletnico. Predvajala sem mu jo v avtu. Ko je slišal kako pesem govori o najinem prvem srečanju in o njegovem pogledu ki me je rešil… je moral ustavit avto ker ni več videl od solz. Darilo ki ga nobena zapestnica ali parfum ne more premagat.' },
              { name: 'Simona P.', img: '/images/reviews/simona.jpg',
                text: 'Pesem sem naročila za svojo hčerko Nejo za njeno 18. rojstni dan. Hotela sem da sliši kako ponosna sem nanjo. Ko je slišala svoj vzdevek v pesmi je začela jokat in me objela tako kot me ni že leta. Besedilo so poslali tudi v PDF-ju — zdaj ga ima uokvirjenega na steni.' },
              { name: 'Kristina D.', img: '/images/reviews/kristina.jpg',
                text: 'Naročila sem pesem za moža za najino 10. obletnico. Napisala sem jim kako sva se spoznala preko njegove mame in kako sem od prvega trenutka vedela da je on moj človek. Pesem je bla v folk stilu, čustvena balada — točno tako kot sem si predstavljala. Mož je reku da je to edino darilo ki ga bo poslušal do konca življenja.' },
              { name: 'Petra L.', img: '/images/reviews/petra.jpg',
                text: 'Za hčerkin rojstni dan sem ji hotela povedati vse kar ji v vsakdanjem življenju pozabim reči. Ko je slišala pesem in slišala kako jo imenujejo "moja pikica" — tisti vzdevek ki ga uporablam od kar je bla majhna — se je zlomila. Jokali sva obe pol ure. Najboljše darilo ki sem ga kdaj dala.' },
            ].map((r, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
                <div className="p-6 flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <img src={r.img} alt={r.name} width="48" height="48" loading="lazy" className="w-12 h-12 rounded-full object-cover border-2 border-gray-100" />
                    <div>
                      <p className="font-bold text-gray-900">{r.name}</p>
                      <div className="flex items-center gap-1">
                        <div className="flex gap-0.5">{[1,2,3,4,5].map(j => <svg key={j} className="w-3.5 h-3.5" fill="#c2590a" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>)}</div>
                        <span className="text-xs text-green-600 ml-1 flex items-center gap-0.5"><svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>{t3('Verified', 'Preverjeno', 'Verificirano')}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">"{r.text}"</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU GET — compact vinyl player */}
      <section className="py-10 md:py-14 px-4" style={{ backgroundColor: '#fdf6ef' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-extrabold text-center mb-2" style={{ fontFamily: 'Georgia, serif' }}>{t3('What You Get', 'Kaj je v vaši pesmi?', 'Što je u vašoj pjesmi?')}</h2>
          <p className="text-gray-500 text-center text-sm mb-8">{t3("Every song is unique — written and produced just for you", 'Vsaka pesem je unikatna — napisana in producirana samo za vas', 'Svaka pjesma je unikatna — napisana i producirana samo za vas')}</p>

          <div className="flex flex-col md:flex-row items-center gap-8">
            {/* Vinyl player */}
            <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-xs">
              <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
              <div className="relative w-40 h-40 mx-auto mb-4">
                <div className="absolute inset-0 rounded-full bg-gray-900" style={playingSample === 'vinyl' ? { animation: 'spin 3s linear infinite' } : {}}>
                  <div className="absolute inset-[15%] rounded-full bg-gray-800" />
                  <div className="absolute inset-[28%] rounded-full bg-gray-700" />
                  <div className="absolute inset-[36%] rounded-full border border-gray-500/30" />
                  <div className="absolute inset-[33%] rounded-full flex items-center justify-center overflow-hidden" style={{ backgroundColor: '#fdf6ef' }}>
                    <span className="font-bold text-[10px] italic text-center leading-tight" style={{ fontFamily: 'Georgia, serif', color: '#b45f2a' }}>{samples[vinylTrack]?.title}</span>
                  </div>
                </div>
              </div>
              <p className="text-center text-sm font-semibold text-gray-900 mb-0.5">{samples[vinylTrack]?.title}</p>
              <p className="text-center text-xs text-gray-500 mb-3">{samples[vinylTrack]?.genre} · {samples[vinylTrack]?.mood}</p>
              <div className="flex items-center justify-center gap-5">
                <button aria-label="Previous song" onClick={() => { const prev = (vinylTrack - 1 + samples.length) % samples.length; setVinylTrack(prev); playSample(samples[prev]?.url, 'vinyl'); }}
                  className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors">
                  <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 24 24"><path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/></svg>
                </button>
                <button aria-label="Play song" onClick={() => playSample(samples[vinylTrack]?.url, 'vinyl')} className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#b45f2a' }}>
                  {playingSample === 'vinyl' ? (
                    <svg width="18" height="18" fill="white" viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
                  ) : (
                    <svg width="18" height="18" fill="white" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                  )}
                </button>
                <button aria-label="Next song" onClick={() => { const next = (vinylTrack + 1) % samples.length; setVinylTrack(next); playSample(samples[next]?.url, 'vinyl'); }}
                  className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors">
                  <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 24 24"><path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/></svg>
                </button>
              </div>
            </div>

            {/* 3 features */}
            <div className="flex-1 space-y-5">
              {[
                { icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" /></svg>, t: t3('Professional Production', 'Profesionalna produkcija', 'Profesionalna produkcija'), d: t3('Studio-quality sound you can proudly share on social media or play at a birthday party.', 'Studijska kvaliteta zvoka ki jo lahko z ponosom delite na družabnih omrežjih ali predvajate na rojstnem dnevu.', 'Studijska kvaliteta zvuka koju možete ponosno dijeliti na društvenim mrežama ili pustiti na rođendanu.') },
                { icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>, t: t3('Your Story, Your Words', 'Vaša zgodba, vaše besede', 'Vaša priča, vaše riječi'), d: t3('Lyrics that mention real names, real memories, and real moments. Not generic — it\'s YOURS.', 'Besedilo ki omenja prava imena, prave spomine in prave trenutke. Ni generično — je VAŠE.', 'Tekst koji spominje prava imena, prave uspomene i prave trenutke. Nije generičko — VAŠE je.') },
                { icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>, t: t3('Fast & Easy', 'Hitro in enostavno', 'Brzo i jednostavno'), d: t3('Place your order in 2 minutes. Receive your song within 24 hours.', 'Oddajte naročilo v 2 minutah. Prejmite pesem v 24 urah.', 'Napravite narudžbu u 2 minute. Primite pjesmu za 24 sata.') },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center text-white" style={{ backgroundColor: '#b45f2a' }}>{item.icon}</div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-0.5">{item.t}</h3>
                    <p className="text-gray-500 text-sm">{item.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 30-DAY GUARANTEE */}
      <section className="py-8 md:py-12 px-4" style={{ backgroundColor: '#fdf6ef' }}>
        <div className="max-w-2xl mx-auto bg-green-50 border border-green-200 rounded-2xl p-6 md:p-8 text-center">
          <svg className="w-10 h-10 text-green-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
          <h3 className="text-xl font-bold text-green-800 mb-2">{t3('30-Day Money-Back Guarantee', '30-dnevna garancija vračila denarja', '30-dnevna garancija povrata novca')}</h3>
          <p className="text-green-700 text-sm">{t3("Not satisfied? Email us and we'll give you a full refund.", 'Niste zadovoljni? Pišite nam in vam vrnemo denar.', 'Niste zadovoljni? Pišite nam i vratit ćemo vam novac.')}</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-20 px-4" style={{ backgroundColor: '#fdf6ef' }}>
        <div className="max-w-3xl mx-auto">
          <p className="text-center text-xs text-gray-500 mb-2">{t3('Risk-Free Purchase · 30 day money back guarantee', 'Brez tveganja · 30-dnevna garancija', 'Bez rizika · 30-dnevna garancija')}</p>
          <h2 className="text-3xl font-bold text-center mb-12">{t3('Frequently Asked Questions', 'Pogosta vprašanja', 'Česta pitanja')}</h2>
          {(hr ? [
            { q: 'Hoće li pjesma zaista zvučati profesionalno?', a: 'Da. Svaka pjesma je producirana u studijskoj kvaliteti s profesionalnim vokalom. Nije "karaoke" zvuk — to je prava pjesma koju možete ponosno pustiti pred svima.' },
            { q: 'Mogu li odabrati žanr?', a: 'Naravno. Pop, folk, rap, balada, turbofolk, R&B — recite nam što sluša osoba za koju naručujete i mi napravimo pjesmu u tom stilu.' },
            { q: 'Što ako mi se pjesma ne svidi?', a: 'Imate 30-dnevnu garanciju povrata novca. Ali u 97% slučajeva klijenti plaču od sreće, ne od razočaranja.' },
            { q: 'Koliko brzo dobijem pjesmu?', a: 'Basic narudžba: do 3 dana. VIP narudžba: do 24 sata. Premium narudžba: do 4 sata. Idealno za poklone u zadnji trenutak.' },
            { q: 'Mogu li pustiti pjesmu na vjenčanju/rođendanu?', a: 'Da! Pjesma je vaša zauvijek. Pustite je bilo gdje i koliko god puta želite.' },
            { q: 'Što trebam za narudžbu?', a: 'Samo 2 minute vremena. Recite nam ime osobe, vaš odnos, neku uspomenu ili emociju, i koji žanr želite. Mi napravimo ostalo.' },
          ] : sl ? [
            { q: 'Ali bo pesem res zvenela profesionalno?', a: 'Da. Vsaka pesem je producirana v studijski kvaliteti z profesionalnim vokalom. Ni "karaoke" zvok — je prava pesem ki jo lahko z ponosom predvajate pred vsemi.' },
            { q: 'Ali lahko izberem žanr?', a: 'Seveda. Pop, folk, rap, balada, turbofolk, R&B — povejte nam kaj posluša oseba za katero naročate in mi naredimo pesem v tistem stilu.' },
            { q: 'Kaj če mi pesem ne bo všeč?', a: 'Imate 30-dnevno garancijo vračila denarja. Ampak v 97% primerov stranke jočejo od sreče, ne od razočaranja.' },
            { q: 'Kako hitro dobim pesem?', a: 'Basic naročilo: do 3 dni. VIP naročilo: do 24 ur. Premium naročilo: do 4 ure. Idealno za darila v zadnjem trenutku.' },
            { q: 'Ali lahko pesem predvajam na poroki/rojstnem dnevu?', a: 'Ja! Pesem je vaša za vedno. Predvajajte jo kjerkoli in kolikokrat želite.' },
            { q: 'Kaj rabim za naročilo?', a: 'Samo 2 minuti časa. Povejte nam ime osebe, vajin odnos, kakšen spomin ali čustvo, in kateri žanr želite. Mi naredimo ostalo.' },
          ] : [
            { q: 'Will the song really sound professional?', a: 'Yes. Every song is produced in studio quality with professional vocals. It\'s not "karaoke" sound — it\'s a real song you can proudly play for everyone.' },
            { q: 'Can I choose the genre?', a: 'Of course. Pop, folk, rap, ballad, R&B — tell us what the person listens to and we\'ll create a song in that style.' },
            { q: 'What if I don\'t like the song?', a: 'You have a 30-day money-back guarantee. But in 97% of cases, customers cry from happiness, not disappointment.' },
            { q: 'How fast do I get the song?', a: 'Basic order: within 3 days. VIP order: within 24 hours. Premium order: within 4 hours. Perfect for last-minute gifts.' },
            { q: 'Can I play the song at a wedding/birthday?', a: 'Yes! The song is yours forever. Play it anywhere, as many times as you want.' },
            { q: 'What do I need to order?', a: 'Just 2 minutes of your time. Tell us the person\'s name, your relationship, a memory or emotion, and which genre you want. We do the rest.' },
          ]).map((item, i) => (
            <details key={i} className="group mb-2">
              <summary className="flex items-center justify-between cursor-pointer rounded-xl bg-white border border-gray-200 px-5 py-4 hover:border-[#d4a574] transition-all">
                <span className="font-medium">{item.q}</span>
                <svg className="w-4 h-4 text-gray-500 group-open:rotate-180 transition-transform flex-shrink-0 ml-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
              </summary>
              <p className="text-gray-500 px-5 py-3 text-sm">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="py-20 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">{t3("Prepare a gift they'll never forget", 'Pripravite darilo, ki ga ne bo pozabil/a', 'Pripremite poklon koji neće zaboraviti')}</h2>
          <p className="text-gray-500 mb-8">{t3('A unique song, created just for your story.', 'Edinstvena pesem, ustvarjena samo za vašo zgodbo.', 'Jedinstvena pjesma, stvorena samo za vašu priču.')}</p>
          <button onClick={startForm} className="text-white font-bold py-4 px-14 rounded-lg text-lg hover:shadow-xl hover:scale-105 transition-all" style={{ backgroundColor: '#b45f2a' }}>
            {t3('Create a song', 'Ustvari pesem', 'Stvori pjesmu')}
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-gray-200 py-12 px-4" style={{ backgroundColor: '#fdf6ef' }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 mb-8">
            <div className="text-center md:text-left">
              <p className="font-bold text-gray-900 text-lg" style={{ fontFamily: 'Georgia, serif' }}>{t3('Ustvari Pesem', 'Ustvari Pesem', 'Stvori Pjesmu')}</p>
              <p className="text-gray-500 text-sm mt-1">{t3('Unique songs for special moments', 'Edinstvene pesmi za posebne trenutke', 'Jedinstvene pjesme za posebne trenutke')}</p>
            </div>
            <div className="flex items-center gap-4">
              {/* WhatsApp */}
              <a href="https://wa.me/38670126994" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-green-500 text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-green-600 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </a>
              {/* Email */}
              <a href="mailto:info@ustvari-pesem.si" className="flex items-center gap-2 bg-gray-800 text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-900 transition-colors">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                Email
              </a>
              {/* SMS */}
              <a href="sms:+38670126994" className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-600 transition-colors">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
                SMS
              </a>
            </div>
          </div>
          {/* Company info — establishes that there's a real, registered Slovenian
              business behind the site. Trust signal for buyers hesitating at checkout. */}
          <div className="pt-6 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-xs text-gray-500 mb-6">
              <div>
                <p className="font-bold text-gray-700 mb-1">{t3('Company', 'Podjetje', 'Tvrtka')}</p>
                <p>BROTHERHOOD d.o.o.</p>
                <p>{t3('Registered in Slovenia 🇸🇮', 'Registrirano v Sloveniji 🇸🇮', 'Registrirano u Sloveniji 🇸🇮')}</p>
              </div>
              <div>
                <p className="font-bold text-gray-700 mb-1">{t3('Contact', 'Kontakt', 'Kontakt')}</p>
                <a href="mailto:info@ustvari-pesem.si" className="block hover:text-gray-700">info@ustvari-pesem.si</a>
                <a href="https://wa.me/38670126994" target="_blank" rel="noopener noreferrer" className="block hover:text-gray-700">+386 70 126 994 (WhatsApp)</a>
              </div>
              <div>
                <p className="font-bold text-gray-700 mb-1">{t3('Payments', 'Plačila', 'Plaćanja')}</p>
                <p>{t3('Stripe — secure & encrypted', 'Stripe — varno in šifrirano', 'Stripe — sigurno i šifrirano')}</p>
                <p>{t3('30-day money-back guarantee', '30-dnevna garancija vračila', '30-dnevna garancija povrata')}</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-xs text-gray-500">© 2026 BROTHERHOOD d.o.o. · {t3('All rights reserved', 'Vse pravice pridržane', 'Sva prava pridržana')}</p>
              <div className="flex gap-4 text-xs text-gray-500">
                <a href="/privacy" className="hover:text-gray-600">{t3('Privacy', 'Zasebnost', 'Privatnost')}</a>
                <a href="/terms" className="hover:text-gray-600">{t3('Terms', 'Pogoji', 'Uvjeti')}</a>
                <a href="https://www.thedubaiprod.com" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600">Powered by DubaiProd</a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp button */}
      <a href="https://wa.me/38670126994" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-green-500 rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 hover:scale-110 transition-all">
        <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
      </a>
    </main>
  );
}
